import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import utils from '../../universal/utils';
import adminFetch from '../../universal/adminFetch';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import resolveUserSteamId from '../users/resolveUserSteamId';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  row: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    marginTop: theme.spacing(2),
  },
  searchFieldWrap: {
    width: '100%',
    maxWidth: 360,
    flex: '0 1 360px',
  },
  randomInput: {
    width: 120,
    flex: '0 0 auto',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  status: {
    marginTop: theme.spacing(2),
  },
  actionRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    flexWrap: 'wrap',
  },
  optionPrimary: {
    fontWeight: 600,
  },
  optionSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '0.82rem',
  },
}));

function formatUserLabel(user) {
  if (!user) {
    return '';
  }

  const nickname = user.nickname || user.name || user.email || 'Unknown user';
  const steamId = resolveUserSteamId(user);
  return steamId ? `${nickname} (${steamId})` : `${nickname} (${user.userId})`;
}

function userMatchesQuery(user, query) {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  return [user?.name, user?.email, user?.nickname, resolveUserSteamId(user), user?.userId]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalizedQuery));
}

export default function UiExperimentsAssignmentsPanel({ userId, onChanged }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [randomCount, setRandomCount] = useState('20');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedUserIds = useMemo(
    () => selectedUsers.map((user) => user.userId).filter(Boolean),
    [selectedUsers]
  );

  const loadUsers = useCallback(async () => {
    if (!userId) {
      return;
    }

    setUsersLoading(true);
    try {
      const response = await adminFetch(`${basePath}/api/v7/admin/users/adpass/${userId}`);
      const data = await response.json();
      if (!data?.ok) {
        throw new Error(data?.err || 'Failed to load users');
      }
      if (isMountedRef.current) {
        setAllUsers(Array.isArray(data.users) ? data.users : []);
      }
    } catch (e) {
      if (isMountedRef.current) {
        setError(e?.message || String(e));
      }
    } finally {
      if (isMountedRef.current) {
        setUsersLoading(false);
      }
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredOptions = useMemo(() => {
    const selectedIds = new Set(selectedUserIds);
    return allUsers
      .filter((user) => user?.userId && !selectedIds.has(user.userId))
      .filter((user) => userMatchesQuery(user, searchInput))
      .slice(0, 20);
  }, [allUsers, searchInput, selectedUserIds]);

  const addSelectedUser = useCallback((user) => {
    if (!user?.userId) {
      return;
    }

    setSelectedUsers((previous) => {
      if (previous.some((entry) => entry.userId === user.userId)) {
        return previous;
      }
      return [...previous, user];
    });
    setSearchInput('');
    setError('');
    setSuccess('');
  }, []);

  const removeSelectedUser = useCallback((targetUserId) => {
    setSelectedUsers((previous) => previous.filter((user) => user.userId !== targetUserId));
  }, []);

  const postAction = useCallback(async (path, body) => {
    if (!userId) return null;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await adminFetch(`${basePath}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!data?.ok) {
        throw new Error(data?.error || 'Request failed');
      }
      if (isMountedRef.current) {
        setSuccess('Action completed successfully.');
        setSelectedUsers([]);
        setSearchInput('');
      }
      onChanged?.();
      return data;
    } catch (e) {
      if (isMountedRef.current) {
        setError(e?.message || String(e));
      }
      return null;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [basePath, isMountedRef, onChanged, userId]);

  const handleAssignSelected = useCallback(async () => {
    if (!selectedUserIds.length) {
      setError('Add at least one user.');
      return;
    }
    await postAction(
      `/api/v7/admin/ui-experiments/v2/assign-users/adpass/${userId}`,
      { userIds: selectedUserIds }
    );
  }, [postAction, selectedUserIds, userId]);

  const handleAssignRandom = useCallback(async () => {
    await postAction(
      `/api/v7/admin/ui-experiments/v2/assign-random/adpass/${userId}`,
      { count: Number.parseInt(randomCount, 10) || 0 }
    );
  }, [postAction, randomCount, userId]);

  return (
    <Paper className={classes.paper} elevation={1}>
      <Typography variant="h6">V2 Design Assignments</Typography>
      <Typography variant="body2" color="textSecondary">
        Search users by nickname or Steam ID, then assign them to receive the lobby prompt.
      </Typography>

      <Box className={classes.row}>
        <Box className={classes.searchFieldWrap}>
          <Autocomplete
            fullWidth
            value={null}
            options={filteredOptions}
            loading={usersLoading}
            inputValue={searchInput}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'reset') {
                setSearchInput('');
                return;
              }
              setSearchInput(newInputValue);
            }}
            onChange={(event, value) => {
              if (value) {
                addSelectedUser(value);
              }
              setSearchInput('');
            }}
            getOptionLabel={formatUserLabel}
            getOptionSelected={(option, value) => option.userId === value.userId}
            filterOptions={(options) => options}
            blurOnSelect
            clearOnBlur
          noOptionsText={
            usersLoading
              ? 'Loading users...'
              : searchInput.trim()
                ? 'No matching users'
                : 'Start typing a nickname or Steam ID'
          }
          renderOption={(user) => (
            <Box>
              <Typography className={classes.optionPrimary}>
                {user.nickname || user.name || user.email || 'Unknown user'}
              </Typography>
              <Typography className={classes.optionSecondary}>
                {resolveUserSteamId(user)
                  ? `Steam ID: ${resolveUserSteamId(user)}`
                  : `User ID: ${user.userId}`}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search user"
              placeholder="Nickname or Steam ID"
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {usersLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          />
        </Box>
        <TextField
          className={classes.randomInput}
          label="Random count"
          type="number"
          value={randomCount}
          onChange={(event) => setRandomCount(event.target.value)}
          variant="outlined"
          size="small"
          inputProps={{ min: 1, max: 500 }}
        />
      </Box>

      {selectedUsers.length ? (
        <Box className={classes.chipRow}>
          {selectedUsers.map((user) => (
            <Chip
              key={user.userId}
              label={formatUserLabel(user)}
              onDelete={() => removeSelectedUser(user.userId)}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      ) : (
        <Typography className={classes.status} variant="body2" color="textSecondary">
          No users selected yet.
        </Typography>
      )}

      <Box className={classes.actionRow}>
        <Button
          variant="contained"
          color="primary"
          disabled={loading || !selectedUserIds.length}
          onClick={handleAssignSelected}
        >
          Assign selected
        </Button>
        <Button
          variant="contained"
          color="default"
          disabled={loading}
          onClick={handleAssignRandom}
        >
          Assign random
        </Button>
        {loading ? <CircularProgress size={24} /> : null}
      </Box>

      {error ? (
        <Typography className={classes.status} color="error">
          {error}
        </Typography>
      ) : null}
      {success ? (
        <Typography className={classes.status} style={{ color: '#66bb6a' }}>
          {success}
        </Typography>
      ) : null}
    </Paper>
  );
}

UiExperimentsAssignmentsPanel.propTypes = {
  userId: PropTypes.string,
  onChanged: PropTypes.func,
};
